import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import {
  partialRetractTimeOffRequest,
  retractTimeOffRequest,
} from '../store/slices/Requestor/timeOffDetailsSlice'
import { useAppDispatch } from '../store/hooks'

interface UseRetractRequestParams {
  requestId: string
  fallbackNuid?: string
}

interface UseRetractRequestResult {
  partialRetract: boolean
  setPartialRetract: Dispatch<SetStateAction<boolean>>
  comments: string
  setComments: Dispatch<SetStateAction<string>>
  retractError: string | null
  isSubmittingRetract: boolean
  retractSubmitted: boolean
  setRetractSubmitted: Dispatch<SetStateAction<boolean>>
  submitRetractRequest: () => Promise<boolean>
  submitPartialRetractRequest: (partialRetractIds: number[]) => Promise<boolean>
  resetRetractForm: () => void
}

/**
 * Centralizes retract-request form state and submit behavior.
 *
 * It submits the retract request and relies on the thunk to refresh latest TOR details.
 */
export const useRetractRequest = ({
  requestId,
  fallbackNuid,
}: UseRetractRequestParams): UseRetractRequestResult => {
  const dispatch = useAppDispatch()
  const [partialRetract, setPartialRetract] = useState(false)
  const [comments, setComments] = useState('')
  const [retractError, setRetractError] = useState<string | null>(null)
  const [isSubmittingRetract, setIsSubmittingRetract] = useState(false)
  const [retractSubmitted, setRetractSubmitted] = useState(false)

  const resolveEmpNuid = () =>
    (sessionStorage.getItem('userId') ?? fallbackNuid ?? '').trim().toUpperCase()

  /**
   * Shared execution wrapper that manages submitting/success/error state.
   * Callers supply the async `action` to dispatch and a fallback error message.
   */
  const executeSubmit = async (
    action: () => Promise<unknown>,
    fallbackMessage: string
  ): Promise<boolean> => {
    setRetractError(null)
    setIsSubmittingRetract(true)
    try {
      await action()
      setRetractSubmitted(true)
      setComments('')
      return true
    } catch (error) {
      setRetractError(error instanceof Error ? error.message : fallbackMessage)
      return false
    } finally {
      setIsSubmittingRetract(false)
    }
  }

  const submitRetractRequest = async (): Promise<boolean> => {
    const empNuid = resolveEmpNuid()
    if (!requestId || !empNuid) {
      setRetractError('Unable to submit retraction request. Missing request ID or employee NUID.')
      return false
    }
    return executeSubmit(
      () =>
        dispatch(
          retractTimeOffRequest({
            requestId,
            empNuid,
            mgrRequestedRetraction: partialRetract ? 'Y' : 'N',
            comments: comments.trim() || null,
          })
        ).unwrap(),
      'Failed to submit retraction request.'
    )
  }

  const submitPartialRetractRequest = async (partialRetractIds: number[]): Promise<boolean> => {
    const empNuid = resolveEmpNuid()
    if (!requestId || !empNuid) {
      setRetractError('Unable to submit retraction request. Missing request ID or employee NUID.')
      return false
    }
    if (partialRetractIds.length === 0) {
      setRetractError('Please select at least one date to partially retract.')
      return false
    }
    return executeSubmit(
      () =>
        dispatch(
          partialRetractTimeOffRequest({
            requestId,
            empNuid,
            mgrRequestedRetraction: partialRetract ? 'Y' : 'N',
            partialRetractIds,
            comments: comments.trim() || null,
          })
        ).unwrap(),
      'Failed to submit partial retraction request.'
    )
  }

  const resetRetractForm = useCallback(() => {
    setPartialRetract(false)
    setComments('')
    setRetractError(null)
  }, [])

  return {
    partialRetract,
    setPartialRetract,
    comments,
    setComments,
    retractError,
    isSubmittingRetract,
    retractSubmitted,
    setRetractSubmitted,
    submitRetractRequest,
    submitPartialRetractRequest,
    resetRetractForm,
  }
}
